"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Hierarchy = void 0;
const classes_1 = require("./classes");
const clone_1 = require("./clone");
const component_1 = __importDefault(require("./component"));
const proxy_1 = require("./proxy");
const tx_1 = require("./tx");
/**
 * @public
 */
class Hierarchy {
    parent;
    classifiers = new Map();
    attributes = new Map();
    attributesById = new Map();
    // Derived on demand: a class may be created before its parent, and an eagerly derived
    // chain would stay truncated forever.
    descendants = new Map();
    ancestors = new Map();
    // Instances owned by ModelDb: it applies their updates, doing it here too would double $push/$inc.
    externallyOwned = new Set();
    // Classifiers and attributes of the shared parent hidden here: the parent itself stays untouched.
    removed = new Set();
    inheritedDomains = new Map();
    // A parent classifier re-created/updated/removed here may have changed the parent's descendant lists.
    parentOverridden = false;
    /** Shared system hierarchy this one extends: own entries win, the rest is read from it. */
    constructor(parent) {
        this.parent = parent;
    }
    findClassifier(_id) {
        const own = this.classifiers.get(_id);
        if (own !== undefined)
            return own;
        return this.removed.has(_id) ? undefined : this.parent?.findClassifier(_id);
    }
    hasClassifier(_id) {
        if (this.classifiers.has(_id))
            return true;
        return this.removed.has(_id) ? false : (this.parent?.hasClassifier(_id) ?? false);
    }
    classifierIds() {
        if (this.parent === undefined) {
            return this.classifiers.keys();
        }
        const ids = new Set();
        for (const _id of this.parent.classifierIds()) {
            if (!this.removed.has(_id))
                ids.add(_id);
        }
        for (const _id of this.classifiers.keys()) {
            ids.add(_id);
        }
        return ids.values();
    }
    /** Own map of a class is complete (parent's entries copied in on first write), so no merge per call. */
    ownAttributesOf(_class) {
        return this.attributes.get(_class) ?? this.parent?.ownAttributesOf(_class);
    }
    ownAttributeMap(_class) {
        let attributes = this.attributes.get(_class);
        if (attributes === undefined) {
            attributes = new Map(this.parent?.ownAttributesOf(_class));
            this.attributes.set(_class, attributes);
        }
        return attributes;
    }
    findAttributeById(_id) {
        const own = this.attributesById.get(_id);
        if (own !== undefined)
            return own;
        return this.removed.has(_id) ? undefined : this.parent?.findAttributeById(_id);
    }
    /** Copy-on-write: an attribute owned by the shared parent must not be mutated in place. */
    ownAttribute(_id) {
        const own = this.attributesById.get(_id);
        if (own !== undefined)
            return own;
        const inherited = this.findAttributeById(_id);
        if (inherited === undefined)
            return undefined;
        const copy = (0, clone_1.clone)(inherited);
        this.addAttribute(copy);
        return copy;
    }
    /** Copy-on-write: a classifier owned by the shared parent must not be mutated in place. */
    ownClassifier(_id) {
        const own = this.classifiers.get(_id);
        if (own !== undefined)
            return own;
        const inherited = this.findClassifier(_id);
        if (inherited === undefined)
            return undefined;
        const copy = (0, clone_1.clone)(inherited);
        this.classifiers.set(_id, copy);
        this.invalidateChains();
        return copy;
    }
    proxies = new Map();
    classifierProperties = new Map();
    createMixinProxyHandler(mixin) {
        const value = this.getClass(mixin);
        const ancestor = this.getClass(value.extends);
        const ancestorProxy = ancestor.kind === classes_1.ClassifierKind.MIXIN ? this.getMixinProxyHandler(ancestor._id) : null;
        return (0, proxy_1._createMixinProxy)(value, ancestorProxy);
    }
    getMixinProxyHandler(mixin) {
        const handler = this.proxies.get(mixin);
        if (handler === undefined) {
            const handler = this.createMixinProxyHandler(mixin);
            this.proxies.set(mixin, handler);
            return handler;
        }
        return handler;
    }
    /**
     * Proxy get-invariant: a frozen own property must be returned as is, so a mixin value may not
     * shadow a root key of the same name. Only such a collision forces an unfrozen copy.
     */
    shadowsFrozenKey(target, mixin) {
        // The proxy falls back to ancestor mixins, so their values collide the same way.
        for (const _class of this.getAncestors(mixin)) {
            const value = target[_class];
            if (typeof value !== 'object' || value === null)
                continue;
            // for-in, not Object.keys: this runs on every mixin read and must not allocate.
            for (const key in value) {
                if (key in target)
                    return true;
            }
        }
        return false;
    }
    as(doc, mixin) {
        if (doc[proxy_1.PROXY_MIXIN_CLASS_KEY] === mixin)
            return doc;
        const handler = this.getMixinProxyHandler(mixin);
        const target = Hierarchy.toDoc(doc);
        const copy = Object.isFrozen(target) && this.shadowsFrozenKey(target, mixin);
        return new Proxy(copy ? { ...target } : target, handler);
    }
    asIf(doc, mixin) {
        if (doc === undefined) {
            return undefined;
        }
        return this.hasMixin(doc, mixin) ? this.as(doc, mixin) : undefined;
    }
    asIfArray(docs, mixin) {
        return docs.map((it) => this.asIf(it, mixin)).filter((it) => it !== undefined);
    }
    static toDoc(doc) {
        return (0, proxy_1._toDoc)(doc);
    }
    static mixinClass(doc) {
        return (0, proxy_1._mixinClass)(doc);
    }
    static mixinOrClass(doc) {
        const m = (0, proxy_1._mixinClass)(doc);
        return m ?? doc._class;
    }
    static hasMixin(doc, mixin) {
        const d = Hierarchy.toDoc(doc);
        return typeof d[mixin] === 'object';
    }
    hasMixin(doc, mixin) {
        return Hierarchy.hasMixin(doc, mixin);
    }
    // M is a mixin on the class, not on the document, so it is not bound to D: mixins such as
    // ObjectPresenter extend Class<Doc>, which never extends the document type D.
    classHierarchyMixin(_class, mixin, filter) {
        let clazz = this.getClass(_class);
        while (true) {
            if (this.hasMixin(clazz, mixin)) {
                const m = this.as(clazz, mixin);
                if (m !== undefined && (filter?.(m) ?? true)) {
                    return m;
                }
            }
            if (clazz.extends === undefined)
                return;
            clazz = this.getClass(clazz.extends);
        }
    }
    findClassOrMixinMixin(doc, mixin) {
        const cc = this.classHierarchyMixin(doc._class, mixin);
        if (cc !== undefined) {
            return cc;
        }
        const _doc = (0, proxy_1._toDoc)(doc);
        // Find all potential mixins of doc
        for (const [k, v] of Object.entries(_doc)) {
            if (typeof v === 'object' && this.hasClassifier(k)) {
                const cc = this.classHierarchyMixin(k, mixin);
                if (cc !== undefined) {
                    return cc;
                }
            }
        }
    }
    findMixinMixins(doc, mixin) {
        const _doc = (0, proxy_1._toDoc)(doc);
        const result = [];
        const resultSet = new Set();
        // Find all potential mixins of doc
        for (const [k, v] of Object.entries(_doc)) {
            if (typeof v === 'object' && this.hasClassifier(k)) {
                const clazz = this.getClass(k);
                if (this.hasMixin(clazz, mixin)) {
                    const cc = this.as(clazz, mixin);
                    if (cc !== undefined && !resultSet.has(cc._id)) {
                        result.push(cc);
                        resultSet.add(cc._id);
                    }
                }
            }
        }
        return result;
    }
    findAllMixins(doc) {
        const _doc = (0, proxy_1._toDoc)(doc);
        const resultSet = new Set();
        for (const [k, v] of Object.entries(_doc)) {
            if (typeof v === 'object' && this.hasClassifier(k)) {
                if (this.isMixin(k)) {
                    if (!resultSet.has(k)) {
                        resultSet.add(k);
                    }
                }
            }
        }
        return Array.from(resultSet);
    }
    getAllPossibleMixins(_class, to = component_1.default.class.Doc) {
        const result = new Set();
        let c = this.getClass(_class);
        while (c._id !== to && c.extends !== undefined) {
            const _descendants = this.getDescendants(c._id);
            for (const d of _descendants) {
                if (this.isMixin(d) && this.getBaseClass(d) === c._id) {
                    result.add(d);
                }
            }
            c = this.getClass(c.extends);
        }
        return [...result];
    }
    isMixin(_class) {
        const data = this.findClassifier(_class);
        return data !== undefined && this._isMixin(data);
    }
    getAncestors(_class) {
        const cached = this.ancestors.get(_class);
        if (cached !== undefined) {
            return cached;
        }
        if (!this.hasClassifier(_class)) {
            throw new Error('ancestors not found: ' + _class);
        }
        // Parent's chain is shared as is unless something on it is overridden or removed here.
        let result = this.classifiers.has(_class) ? undefined : this.parent?.getAncestors(_class);
        if (result?.some((id) => this.classifiers.has(id) || this.removed.has(id)) === true) {
            result = undefined;
        }
        result ??= this.buildAncestors(_class);
        this.ancestors.set(_class, result);
        return result;
    }
    buildAncestors(_class) {
        const cl = [_class];
        const visited = new Set();
        const ancestorList = [];
        while (cl.length > 0) {
            const classifier = cl.shift();
            if (addNew(visited, classifier)) {
                ancestorList.push(classifier);
                cl.push(...this.ancestorsOf(classifier));
            }
        }
        return ancestorList;
    }
    invalidateChains() {
        this.ancestors.clear();
        this.descendants.clear();
        this.inheritedDomains.clear();
    }
    getClass(_class) {
        const data = this.findClassifier(_class);
        if (data === undefined || this.isInterface(data)) {
            throw new Error('class not found: ' + _class);
        }
        return data;
    }
    findClass(_class) {
        const data = this.findClassifier(_class);
        if (data === undefined || this.isInterface(data)) {
            return undefined;
        }
        return data;
    }
    hasClass(_class) {
        const data = this.findClassifier(_class);
        return !(data === undefined || this.isInterface(data));
    }
    getClassOrInterface(_class) {
        const data = this.findClassifier(_class);
        if (data === undefined) {
            throw new Error('class not found: ' + _class);
        }
        return data;
    }
    getInterface(_interface) {
        const data = this.findClassifier(_interface);
        if (data === undefined || !this.isInterface(data)) {
            throw new Error('interface not found: ' + _interface);
        }
        return data;
    }
    getDomain(_class) {
        const domain = this.findDomain(_class);
        if (domain === undefined) {
            throw new Error(`domain not found: ${_class} `);
        }
        return domain;
    }
    findDomain(_class) {
        const klazz = this.findClass(_class);
        if (klazz === undefined)
            return;
        if (klazz.domain !== undefined) {
            return klazz.domain;
        }
        const cached = this.inheritedDomains.get(_class);
        if (cached !== undefined) {
            return cached;
        }
        // Same chain object as the parent's: its domain cache is valid here too.
        if (this.parent !== undefined &&
            !this.classifiers.has(_class) &&
            this.getAncestors(_class) === this.parent.getAncestors(_class)) {
            return this.parent.findDomain(_class);
        }
        let _klazz = klazz;
        while (_klazz.extends !== undefined) {
            _klazz = this.findClass(_klazz.extends);
            if (_klazz === undefined)
                return;
            if (_klazz.domain !== undefined) {
                // Cached aside: writing it into the class would mutate a document shared across workspaces.
                this.inheritedDomains.set(_class, _klazz.domain);
                return _klazz.domain;
            }
        }
    }
    getMixinClasses(mixin) {
        const result = [];
        for (const _id of this.classifierIds()) {
            try {
                if (this.classHierarchyMixin(_id, mixin) !== undefined) {
                    result.push(_id);
                }
            }
            catch (e) {
                // ignore
            }
        }
        return result;
    }
    /**
     * `doc` hands over an instance owned by the caller (ModelDb): it is referenced, not copied,
     * and its updates are left to the owner. Without `doc` the hierarchy owns its documents.
     */
    tx(tx, doc) {
        switch (tx._class) {
            case component_1.default.class.TxCreateDoc:
                this.txCreateDoc(tx, doc);
                return;
            case component_1.default.class.TxUpdateDoc:
                this.txUpdateDoc(tx);
                return;
            case component_1.default.class.TxRemoveDoc:
                this.txRemoveDoc(tx);
                return;
            case component_1.default.class.TxMixin:
                this.txMixin(tx);
        }
    }
    /** Point classifier/attribute indexes at a new instance of a document its owner replaced. */
    replaceDoc(doc) {
        if (this.hasClassifier(doc._id)) {
            this.classifiers.set(doc._id, doc);
            this.classifierProperties.delete(doc._id);
            this.externallyOwned.add(doc._id);
        }
        else if (this.findAttributeById(doc._id) !== undefined) {
            this.addAttribute(doc);
            this.externallyOwned.add(doc._id);
        }
    }
    isClassifierTx(tx) {
        return CLASSIFIER_CLASSES.includes(tx.objectClass) || this.isDerived(tx.objectClass, component_1.default.class.Class);
    }
    txCreateDoc(tx, doc) {
        const isClassifier = this.isClassifierTx(tx);
        if (!isClassifier && tx.objectClass !== component_1.default.class.Attribute)
            return;
        // Without an instance the hierarchy owns its copy again (callers may replay a tx after addTxes).
        if (doc !== undefined)
            this.externallyOwned.add(tx.objectId);
        else
            this.externallyOwned.delete(tx.objectId);
        this.removed.delete(tx.objectId);
        if (isClassifier) {
            const _id = tx.objectId;
            if (this.parent?.hasClassifier(_id) === true)
                this.parentOverridden = true;
            this.classifiers.set(_id, doc ?? tx_1.TxProcessor.createDoc2Doc(tx));
            this.invalidateChains();
        }
        else {
            const createTx = tx;
            this.addAttribute(doc ?? tx_1.TxProcessor.createDoc2Doc(createTx));
        }
    }
    txUpdateDoc(tx) {
        if (tx.objectClass === component_1.default.class.Attribute) {
            const updateTx = tx;
            const doc = this.ownAttribute(updateTx.objectId);
            if (doc === undefined)
                return;
            if (!this.externallyOwned.has(doc._id)) {
                tx_1.TxProcessor.updateDoc2Doc(doc, updateTx);
            }
            this.addAttribute(doc);
            // A rename leaves the attribute under its old name otherwise.
            const map = this.ownAttributeMap(doc.attributeOf);
            for (const [name, attr] of map) {
                if (name !== doc.name && attr._id === doc._id) {
                    const inherited = this.parent?.ownAttributesOf(doc.attributeOf)?.get(name);
                    if (inherited !== undefined && inherited._id !== doc._id && !this.removed.has(inherited._id)) {
                        map.set(name, inherited);
                    }
                    else {
                        map.delete(name);
                    }
                }
            }
            this.classifierProperties.delete(doc.attributeOf);
        }
        else if (this.isClassifierTx(tx)) {
            const updateTx = tx;
            const doc = this.ownClassifier(updateTx.objectId);
            if (doc === undefined)
                return;
            if (!this.externallyOwned.has(doc._id)) {
                tx_1.TxProcessor.updateDoc2Doc(doc, updateTx);
            }
            this.classifierProperties.delete(doc._id);
            // extends/implements may have changed
            if (this.parent?.hasClassifier(doc._id) === true)
                this.parentOverridden = true;
            this.invalidateChains();
        }
    }
    txRemoveDoc(tx) {
        if (tx.objectClass === component_1.default.class.Attribute) {
            const removeTx = tx;
            const doc = this.findAttributeById(removeTx.objectId);
            if (doc === undefined)
                return;
            this.attributesById.delete(removeTx.objectId);
            this.externallyOwned.delete(removeTx.objectId);
            this.removed.add(removeTx.objectId);
            const map = this.ownAttributeMap(doc.attributeOf);
            if (map.get(doc.name)?._id === removeTx.objectId) {
                // A same-named shared attribute becomes visible again once its override is gone.
                const inherited = this.parent?.ownAttributesOf(doc.attributeOf)?.get(doc.name);
                if (inherited !== undefined && inherited._id !== doc._id && !this.removed.has(inherited._id)) {
                    map.set(doc.name, inherited);
                }
                else {
                    map.delete(doc.name);
                }
            }
        }
        else if (this.isClassifierTx(tx)) {
            const removeTx = tx;
            if (this.parent?.hasClassifier(removeTx.objectId) === true)
                this.parentOverridden = true;
            this.classifiers.delete(removeTx.objectId);
            this.externallyOwned.delete(removeTx.objectId);
            this.removed.add(removeTx.objectId);
            this.invalidateChains();
        }
    }
    txMixin(tx) {
        if (this.isClassifierTx(tx)) {
            if (this.externallyOwned.has(tx.objectId))
                return;
            const obj = this.ownClassifier(tx.objectId);
            if (obj === undefined)
                return;
            tx_1.TxProcessor.updateMixin4Doc(obj, tx);
        }
    }
    /**
     * Check if passed _class is derived from `from` class.
     * It will iterate over parents.
     */
    isDerived(_class, from) {
        const cached = this.ancestors.get(_class);
        if (cached !== undefined) {
            return cached.includes(from);
        }
        // Unknown classifiers answer false here rather than throwing, as they always did.
        return this.hasClassifier(_class) ? this.getAncestors(_class).includes(from) : false;
    }
    /**
     * Return first non interface/mixin parent
     */
    getBaseClass(_class) {
        let cl = _class;
        while (cl !== undefined) {
            const clz = this.getClass(cl);
            if (this.isClass(clz))
                return cl;
            cl = clz.extends;
        }
        return component_1.default.class.Doc;
    }
    /**
     * Check if passed _class implements passed interfaces `from`.
     * It will check for class parents and their interfaces.
     */
    isImplements(_class, from) {
        let cl = _class;
        while (cl !== undefined) {
            const klazz = this.getClass(cl);
            if (this.isExtends(klazz.implements ?? [], from)) {
                return true;
            }
            cl = klazz.extends;
        }
        return false;
    }
    /**
     * Check if interface extends passed interface.
     */
    isExtends(extendsOrImplements, from) {
        const result = [];
        const toVisit = [...extendsOrImplements];
        while (toVisit.length > 0) {
            const ref = toVisit.shift();
            if (ref === from) {
                return true;
            }
            addIf(result, ref);
            toVisit.push(...this.ancestorsOf(ref));
        }
        return false;
    }
    /** An unknown classifier simply has no descendants - queries may still reference removed classes. */
    getDescendants(_class) {
        const cached = this.descendants.get(_class);
        if (cached !== undefined) {
            return cached;
        }
        if (!this.hasClassifier(_class)) {
            return [];
        }
        let result;
        if (this.parent !== undefined && !this.parentOverridden) {
            // Parent chains are intact: its list plus own classes deriving from _class.
            const inherited = this.parent.getDescendants(_class);
            const own = [];
            for (const id of this.classifiers.keys()) {
                if (!this.parent.hasClassifier(id) && this.getAncestors(id).includes(_class))
                    own.push(id);
            }
            result = own.length === 0 ? inherited : [...inherited, ...own];
        }
        else {
            result = [];
            for (const classifier of this.classifierIds()) {
                if (this.getAncestors(classifier).includes(_class)) {
                    result.push(classifier);
                }
            }
        }
        this.descendants.set(_class, result);
        return result;
    }
    /**
     * Return extends and implemnets as combined list of references
     */
    ancestorsOf(classifier) {
        const attrs = this.findClassifier(classifier);
        const result = [];
        if (this.isClass(attrs) || this._isMixin(attrs)) {
            const cls = attrs;
            if (cls.extends !== undefined) {
                result.push(cls.extends);
            }
            result.push(...(cls.implements ?? []));
        }
        if (this.isInterface(attrs)) {
            result.push(...(attrs.extends ?? []));
        }
        return result;
    }
    isClass(attrs) {
        return attrs?.kind === classes_1.ClassifierKind.CLASS;
    }
    _isMixin(attrs) {
        return attrs?.kind === classes_1.ClassifierKind.MIXIN;
    }
    isInterface(attrs) {
        return attrs?.kind === classes_1.ClassifierKind.INTERFACE;
    }
    addAttribute(attribute) {
        this.ownAttributeMap(attribute.attributeOf).set(attribute.name, attribute);
        this.attributesById.set(attribute._id, attribute);
        this.classifierProperties.delete(attribute.attributeOf);
    }
    getAllAttributes(clazz, to, traverse) {
        const result = new Map();
        let ancestors = this.getAncestors(clazz);
        if (to !== undefined) {
            const toAncestors = this.getAncestors(to);
            for (const uto of toAncestors) {
                if (ancestors.includes(uto)) {
                    to = uto;
                    break;
                }
            }
            ancestors = ancestors.filter((c) => c !== to && (this.isInterface(this.findClassifier(c)) || this.isDerived(c, to)));
        }
        for (let index = ancestors.length - 1; index >= 0; index--) {
            const cls = ancestors[index];
            const attributes = this.ownAttributesOf(cls);
            if (attributes !== undefined) {
                for (const [name, attr] of attributes) {
                    traverse?.(name, attr);
                    result.set(name, attr);
                }
            }
        }
        return result;
    }
    getOwnAttributes(clazz) {
        const result = new Map();
        const attributes = this.ownAttributesOf(clazz);
        if (attributes !== undefined) {
            for (const [name, attr] of attributes) {
                result.set(name, attr);
            }
        }
        return result;
    }
    getParentClass(_class) {
        const baseDomain = this.getDomain(_class);
        const ancestors = this.getAncestors(_class);
        let result = _class;
        for (const ancestor of ancestors) {
            try {
                const domain = this.getClass(ancestor).domain;
                if (domain === baseDomain) {
                    result = ancestor;
                }
            }
            catch { }
        }
        return result;
    }
    getAttribute(classifier, name) {
        const attr = this.findAttribute(classifier, name);
        if (attr === undefined) {
            throw new Error('attribute not found: ' + name);
        }
        return attr;
    }
    findAttribute(classifier, name) {
        const list = [classifier];
        const visited = new Set();
        while (list.length > 0) {
            const cl = list.shift();
            if (addNew(visited, cl)) {
                const attribute = this.ownAttributesOf(cl)?.get(name);
                if (attribute !== undefined) {
                    return attribute;
                }
                // Check ancestorsOf
                list.push(...this.ancestorsOf(cl));
            }
        }
    }
    updateLookupMixin(_class, result, options) {
        const baseClass = this.getBaseClass(_class);
        const vResult = baseClass !== _class ? this.as(result, _class) : result;
        const lookup = result.$lookup;
        if (lookup !== undefined) {
            // We need to check if lookup type is mixin and cast to it if required.
            const lu = options?.lookup;
            if (lu?._id !== undefined) {
                for (const [k, v] of Object.entries(lu._id)) {
                    const _cl = getClass(v);
                    if (this.isMixin(_cl)) {
                        const mval = lookup[k];
                        if (mval !== undefined) {
                            if (Array.isArray(mval)) {
                                ;
                                lookup[k] = mval.map((it) => this.as(it, _cl));
                            }
                            else {
                                ;
                                lookup[k] = this.as(mval, _cl);
                            }
                        }
                    }
                }
            }
            for (const [k, v] of Object.entries(lu ?? {})) {
                if (k === '_id') {
                    continue;
                }
                const _cl = getClass(v);
                if (this.isMixin(_cl)) {
                    const mval = lookup[k];
                    if (mval != null) {
                        ;
                        lookup[k] = this.as(mval, _cl);
                    }
                }
            }
        }
        return vResult;
    }
    clone(obj) {
        return (0, clone_1.clone)(obj, (doc, m) => this.as(doc, m), (value) => Hierarchy.mixinClass(value));
    }
    domains() {
        const classes = Array.from(this.classifierIds(), (_id) => this.findClassifier(_id)).filter((it) => it !== undefined && (this.isClass(it) || this._isMixin(it)));
        return classes
            .map((it) => it.domain)
            .filter((it) => it !== undefined)
            .filter((it, idx, array) => array.findIndex((pt) => pt === it) === idx);
    }
    getClassifierProp(cl, prop) {
        return this.classifierProperties.get(cl)?.get(prop);
    }
    setClassifierProp(cl, prop, value) {
        let cur = this.classifierProperties.get(cl);
        if (cur === undefined) {
            cur = new Map();
            this.classifierProperties.set(cl, cur);
        }
        cur.set(prop, value);
    }
}
exports.Hierarchy = Hierarchy;
const CLASSIFIER_CLASSES = [component_1.default.class.Class, component_1.default.class.Mixin, component_1.default.class.Interface];
function addNew(val, value) {
    if (val.has(value)) {
        return false;
    }
    val.add(value);
    return true;
}
function addIf(array, value) {
    if (!array.includes(value)) {
        array.push(value);
    }
}
function getClass(vvv) {
    if (Array.isArray(vvv)) {
        return vvv[0];
    }
    return vvv;
}
//# sourceMappingURL=hierarchy.js.map